<template>
    <div class="event-verify">
        <div class="event-verify__head">
            <h3>{{ langJson('Verifiers’ Links') }}</h3>
            <div class="event-verify__tools">
                <input v-if="searching" ref="search" v-model="query" class="event-verify__search" :placeholder="langJson('Search by name or ID')" @keyup.esc="closeSearch" />
                <button type="button" class="event-verify__icon" :title="langJson('Search')" @click="toggleSearch">⌕</button>
                <el-dropdown trigger="click" @command="(t) => (typeFilter = t)">
                    <button type="button" :class="['event-verify__icon', typeFilter && 'event-verify__icon--on']" :title="langJson('Filter by type')">⏷</button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="">{{ langJson('All types') }}</el-dropdown-item>
                        <el-dropdown-item v-for="t in types" :key="t.value" :command="t.value">{{ langJson(t.label) }}</el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
                <button type="button" class="event-verify__icon" :title="langJson('Export CSV')" :disabled="!links.length" @click="exportCsv">⇩</button>
                <button type="button" class="event-verify__icon event-verify__icon--primary" :title="langJson('New link')" @click="openCreate">+</button>
            </div>
        </div>

        <p v-if="loading" class="event-verify__empty">{{ langJson('Loading…') }}</p>
        <p v-else-if="!links.length" class="event-verify__empty">{{ langJson('No verifier links yet. Create one for each gate — staff open it on their phone to scan tickets.') }}</p>
        <p v-else-if="!visible.length" class="event-verify__empty">{{ langJson('No links match.') }}</p>

        <table v-else-if="!isPhone" class="event-verify__table">
            <thead>
                <tr>
                    <th>{{ langJson('Link ID') }}</th>
                    <th>{{ langJson('Name') }}</th>
                    <th>{{ langJson('Type') }}</th>
                    <th>{{ langJson('Link') }}</th>
                    <th>{{ langJson('Valid') }}</th>
                    <th>{{ langJson('Invalid') }}</th>
                    <th>{{ langJson('Used') }}</th>
                    <th>{{ langJson('Status') }}</th>
                    <th>{{ langJson('Action') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="link in visible" :key="link.id" :class="link.revokedAt && 'event-verify__row--revoked'">
                    <td>{{ link.id }}</td>
                    <td>{{ link.label || '—' }}</td>
                    <td>{{ langJson(typeLabel(link.type)) }}</td>
                    <td>
                        <span class="event-verify__link">
                            <a :href="linkUrl(link)" target="_blank" rel="noopener">{{ shortLink(link) }}</a>
                            <button type="button" class="event-verify__mini" :title="langJson('Copy link')" @click="copyLink(link)">⧉</button>
                        </span>
                    </td>
                    <td>{{ link.counts.valid }}</td>
                    <td>{{ link.counts.invalid }}</td>
                    <td>{{ link.counts.used }}</td>
                    <td><span :class="['event-verify__status', link.revokedAt ? 'event-verify__status--off' : 'event-verify__status--on']">{{ langJson(link.revokedAt ? 'Inactive' : 'Active') }}</span></td>
                    <td>
                        <el-dropdown trigger="click" @command="(cmd) => onAction(cmd, link)">
                            <button type="button" class="event-verify__mini">•••</button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="copy">{{ langJson('Copy link') }}</el-dropdown-item>
                                <el-dropdown-item v-if="!link.revokedAt" command="revoke">{{ langJson('Deactivate') }}</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-else class="event-verify__cards">
            <div v-for="link in visible" :key="link.id" :class="['event-verify__card', link.revokedAt && 'event-verify__row--revoked']">
                <div class="event-verify__card-head">
                    <strong>{{ link.label || `#${link.id}` }}</strong>
                    <span :class="['event-verify__status', link.revokedAt ? 'event-verify__status--off' : 'event-verify__status--on']">{{ langJson(link.revokedAt ? 'Inactive' : 'Active') }}</span>
                </div>
                <span class="event-verify__card-meta">{{ langJson(typeLabel(link.type)) }} · {{ link.counts.valid }} {{ langJson('valid') }} · {{ link.counts.invalid }} {{ langJson('invalid') }} · {{ link.counts.used }} {{ langJson('used') }}</span>
                <div class="event-verify__card-actions">
                    <BTN type="detail" bh="36" @handle="copyLink(link)">{{ langJson('Copy link') }}</BTN>
                    <BTN v-if="!link.revokedAt" type="warn" bh="36" @handle="onAction('revoke', link)">{{ langJson('Deactivate') }}</BTN>
                </div>
            </div>
        </div>

        <el-dialog :title="langJson('New verifier link')" :visible.sync="creating" :width="isPhone ? '92%' : '440px'" append-to-body>
            <div class="event-verify__form">
                <FormItem v-model="form.label" type="text" :matchs="['unull']" :title="langJson('Name')" :placeholder="langJson('e.g. Main Entrance 1-3')"></FormItem>
                <div>
                    <div class="event-verify__label">{{ langJson('Type') }}</div>
                    <div class="event-verify__pills">
                        <button v-for="t in types" :key="t.value" type="button" :class="['event-verify__pill', form.type === t.value && 'event-verify__pill--active']" @click="form.type = t.value">{{ langJson(t.label) }}</button>
                    </div>
                    <p class="event-verify__hint">{{ langJson(typeHint) }}</p>
                </div>
            </div>
            <div slot="footer" class="event-verify__dialog-actions">
                <BTN type="detail" bh="40" @handle="creating = false">{{ langJson('Cancel') }}</BTN>
                <BTN type="active" bh="40" :disable="!form.label.trim()" :load="saving" @handle="create">{{ langJson('Create link') }}</BTN>
            </div>
        </el-dialog>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage } from '@/assets/js/organizerEvent'

const TYPES = [
    { value: 'entry', label: 'Entry', hint: 'Scans tickets in at the door.' },
    { value: 'exit', label: 'Exit', hint: 'Scans tickets out, so they can come back in later.' },
    { value: 'both', label: 'Entry & exit', hint: 'One link that can scan in and out.' },
]

/** Ticket Verification step: the event's verifier links (one per gate) and how each is being used. */
export default {
    props: {
        event: { type: Object, required: true },
    },
    data() {
        return {
            links: [],
            loading: true,
            query: '',
            searching: false,
            typeFilter: '',
            creating: false,
            saving: false,
            form: { label: '', type: 'entry' },
            types: TYPES,
        }
    },
    computed: {
        visible() {
            const q = this.query.trim().toLowerCase()
            return this.links.filter(
                (link) => (!this.typeFilter || link.type === this.typeFilter) && (!q || String(link.id).includes(q) || (link.label || '').toLowerCase().includes(q)),
            )
        },
        typeHint() {
            return TYPES.find((t) => t.value === this.form.type).hint
        },
    },
    created() {
        this.load()
    },
    methods: {
        async load() {
            try {
                const res = await organizerApi.listVerifierLinks(this.event.id)
                this.links = res.data
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not load verifier links')))
            }
            this.loading = false
        },
        typeLabel(type) {
            return (TYPES.find((t) => t.value === type) || TYPES[2]).label
        },
        linkUrl(link) {
            return `${window.location.origin}/checkin?token=${link.token}`
        },
        shortLink(link) {
            return `nftw…${link.token.slice(-4)}`
        },
        async copyLink(link) {
            try {
                await navigator.clipboard.writeText(this.linkUrl(link))
            } catch (e) {
                this.copy(this.linkUrl(link))
            }
            this.pushMsg('success', this.langJson('Link copied — anyone with it can scan tickets for this event'))
        },
        toggleSearch() {
            this.searching = !this.searching
            if (this.searching) this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
            else this.query = ''
        },
        closeSearch() {
            this.searching = false
            this.query = ''
        },
        openCreate() {
            this.form = { label: '', type: 'entry' }
            this.creating = true
        },
        async create() {
            this.saving = true
            try {
                await organizerApi.createVerifierLink(this.event.id, { label: this.form.label.trim(), type: this.form.type })
                this.creating = false
                await this.load()
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not create the link')))
            }
            this.saving = false
        },
        async onAction(cmd, link) {
            if (cmd === 'copy') return this.copyLink(link)
            if (cmd !== 'revoke') return
            try {
                await this.$confirm(this.langJson('Staff using this link won’t be able to scan any more, and will be asked for a new link. This can’t be undone.'), `${this.langJson('Deactivate')} “${link.label || link.id}”?`, {
                    confirmButtonText: this.langJson('Deactivate'),
                    cancelButtonText: this.langJson('Cancel'),
                    type: 'warning',
                })
            } catch (e) {
                return
            }
            try {
                await organizerApi.revokeVerifierLink(this.event.id, link.id)
                await this.load()
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not deactivate the link')))
            }
        },
        async exportCsv() {
            try {
                const blob = await organizerApi.exportVerifierLinks(this.event.id)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `verifier-links-event-${this.event.id}.csv`
                a.click()
                URL.revokeObjectURL(url)
            } catch (err) {
                this.pushMsg('error', this.langJson('Could not export the links'))
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.event-verify {
    background: var(--color_white);
    border-radius: 32px;
    padding: 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;

        h3 {
            font-family: 'Inter';
            font-weight: 700;
            font-size: 18px;
            color: var(--font_deep_color);
        }
    }
    &__tools {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    &__icon {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        border: none;
        background: var(--bg_color);
        color: var(--font_deep_color);
        font-size: 18px;
        cursor: pointer;

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
        &--on {
            background: #f1e9ff;
            color: #8b5cf6;
        }
        &--primary {
            background: #f1e9ff;
            color: #8b5cf6;
            font-weight: 700;
        }
    }
    &__search {
        height: 40px;
        border: none;
        border-radius: 50px;
        background: var(--bg_color);
        padding: 0 16px;
        font-family: 'Lato';
        min-width: 200px;
    }
    &__empty {
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_modif_color);
        padding: 24px 0;
        text-align: center;
    }
    &__table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_deep_color);

        th {
            background: var(--bg_color);
            font-weight: 400;
            color: var(--font_color);
            padding: 10px 8px;
            text-align: center;

            &:first-child {
                border-radius: 50px 0 0 50px;
            }
            &:last-child {
                border-radius: 0 50px 50px 0;
            }
        }
        td {
            color: var(--font_deep_color);
            padding: 14px 8px;
            text-align: center;
        }
    }
    &__row--revoked {
        opacity: 0.55;
    }
    &__link {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        a {
            color: #2f80ed;
            font-weight: 700;
            text-decoration: none;
        }
    }
    &__mini {
        border: none;
        background: #f1e9ff;
        color: #8b5cf6;
        border-radius: 8px;
        padding: 4px 8px;
        cursor: pointer;
    }
    &__status {
        padding: 4px 12px;
        border-radius: 50px;
        font-size: 12px;

        &--on {
            background: #e7f8ee;
            color: #12a150;
        }
        &--off {
            background: #fde8e8;
            color: #e5484d;
        }
    }
    &__cards {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    &__card {
        border: 1px solid var(--border_color);
        border-radius: 16px;
        padding: 12px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    &__card-head {
        color: var(--font_deep_color); // otherwise inherits the white page text in dark mode
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-family: 'Lato';
    }
    &__card-meta {
        font-family: 'Lato';
        font-size: 13px;
        color: var(--font_color);
    }
    &__card-actions {
        display: flex;
        gap: 8px;
    }
    &__form {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
    &__label {
        font-family: 'Lato';
        font-size: 14px;
        margin-bottom: 8px;
        color: var(--font_color_black);
    }
    &__pills {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    &__pill {
        border: 1.5px solid transparent;
        background: var(--bg_color);
        border-radius: 50px;
        padding: 8px 16px;
        font-family: 'Lato';
        cursor: pointer;

        &--active {
            background: #f1e9ff;
            border-color: #a855f7;
            font-weight: 700;
        }
    }
    &__dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }
    &__hint {
        margin-top: 6px;
        font-family: 'Lato';
        font-size: 12px;
        color: var(--font_modif_color);
    }
}

@media only screen and (max-width: 1080px) {
    .event-verify {
        padding: 16px;
        border-radius: 24px;

        &__search {
            min-width: 0;
            width: 140px;
        }
    }
}
</style>
